import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getQuickDateRange } from '@/utils/reportDateRanges';
import type { PostingStatus, PostingLogFilters, TimesheetSortKey, SortDir } from '@/utils/PostingLog/types';
import { resolveStatusFilter, toPostingLogQuery } from '@/utils/PostingLog/mappers';

export function usePostingLogFilters() {
  // Filter state
  const [appliedFilters, setAppliedFilters] = useState<PostingLogFilters>({ statuses: ['Failed'] });
  const [workingFilters, setWorkingFilters]  = useState<PostingLogFilters>({ statuses: ['Failed'] });
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickDateRange, setQuickDateRange] = useState('all');

  // Table state
  const [sortKey, setSortKey] = useState<TimesheetSortKey>('weekEnding');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);

  const matchesNothing = resolveStatusFilter(appliedFilters) === null;
  const query = useMemo(
    () => toPostingLogQuery(appliedFilters, debouncedSearch, 'transactionDate', 'desc'),
    [appliedFilters, debouncedSearch],
  );

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleQuickDateChange = (value: string) => {
    setQuickDateRange(value);
    if (value === 'all') {
      setAppliedFilters((f) => { const next = { ...f }; delete next.dateFrom; delete next.dateTo; return next; });
    } else {
      setAppliedFilters((f) => ({ ...f, ...getQuickDateRange(value) }));
    }
    setPage(1);
  };

  const handleQuickStatusChange = (value: string) => {
    if (value === 'all') {
      setAppliedFilters((f) => { const next = { ...f }; delete next.statuses; return next; });
    } else {
      setAppliedFilters((f) => ({ ...f, statuses: [value as PostingStatus] }));
    }
    setPage(1);
  };

  const handleApplySheet = () => { setAppliedFilters(workingFilters); setPage(1); setShowFilterSheet(false); };
  const handleClear = () => { setWorkingFilters({ statuses: ['Failed'] }); setAppliedFilters({ statuses: ['Failed'] }); setQuickDateRange('all'); setSearchQuery(''); setPage(1); };

  const openFilterSheet = () => { setWorkingFilters(appliedFilters); setShowFilterSheet(true); };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.employeeIds?.length) count++;
    if (appliedFilters.dateFrom || appliedFilters.dateTo) count++;
    if (appliedFilters.projectIds?.length) count++;
    if (appliedFilters.costCentreIds?.length) count++;
    if (appliedFilters.departments?.length) count++;
    if (appliedFilters.statuses?.length) count++;
    if (appliedFilters.failedOnly) count++;
    return count;
  }, [appliedFilters]);

  // ── Sheet filter count ─────────────────────────────────────────────────────────
  const sheetFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.employeeIds?.length) count++;
    if (appliedFilters.projectIds?.length) count++;
    if (appliedFilters.costCentreIds?.length) count++;
    if (appliedFilters.departments?.length) count++;
    if (appliedFilters.failedOnly) count++;
    if ((appliedFilters.dateFrom || appliedFilters.dateTo) && quickDateRange === 'all') count++;
    return count;
  }, [appliedFilters, quickDateRange]);

  // ── Sort ──────────────────────────────────────────────────────────────────────
  const handleSort = useCallback((key: TimesheetSortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  }, [sortKey]);

  return {
    appliedFilters, setAppliedFilters, workingFilters, setWorkingFilters,
    showFilterSheet, setShowFilterSheet, searchQuery, setSearchQuery, quickDateRange,
    sortKey, sortDir, page, setPage, query, matchesNothing,
    handleQuickDateChange, handleQuickStatusChange, handleApplySheet, handleClear, handleSort, openFilterSheet,
    activeFilterCount, sheetFilterCount,
  };
}

export type PostingLogFiltersState = ReturnType<typeof usePostingLogFilters>;
